import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { recipeAPI, uploadAPI, boardAPI } from '../services/api';

// Parse leading quantity from ingredient string (e.g. "2 cups flour" -> 2, "1/2 tsp salt" -> 0.5)
function parseQuantity(str) {
  const trimmed = str.trim();
  // Match: optional whole number, optional space + fraction (e.g. "1 1/2"), then rest
  const mixed = trimmed.match(/^\s*(\d+)\s+(\d+)\/(\d+)\s*(.*)$/);
  if (mixed) {
    const whole = parseInt(mixed[1], 10);
    const num = parseInt(mixed[2], 10);
    const denom = parseInt(mixed[3], 10);
    return { value: whole + num / denom, rest: mixed[4].trim() };
  }
  const fraction = trimmed.match(/^\s*(\d+)\/(\d+)\s*(.*)$/);
  if (fraction) {
    const num = parseInt(fraction[1], 10);
    const denom = parseInt(fraction[2], 10);
    return { value: num / denom, rest: fraction[3].trim() };
  }
  const whole = trimmed.match(/^\s*(\d+)\s*(.*)$/);
  if (whole) {
    return { value: parseInt(whole[1], 10), rest: whole[2].trim() };
  }
  return null;
}

function formatScaledQuantity(value) {
  if (value === 0) return '0';
  const tol = 0.001;
  const whole = Math.floor(value);
  const frac = value - whole;
  const f = (n, d) => Math.abs(frac - n / d) < tol;
  if (whole === 0) {
    if (f(1, 2)) return '1/2';
    if (f(1, 3)) return '1/3';
    if (f(2, 3)) return '2/3';
    if (f(1, 4)) return '1/4';
    if (f(3, 4)) return '3/4';
    if (f(1, 8)) return '1/8';
    return value % 1 === 0 ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
  }
  if (Math.abs(frac) < tol) return String(whole);
  if (f(1, 2)) return whole + ' 1/2';
  if (f(1, 3)) return whole + ' 1/3';
  if (f(2, 3)) return whole + ' 2/3';
  if (f(1, 4)) return whole + ' 1/4';
  if (f(3, 4)) return whole + ' 3/4';
  if (f(1, 8)) return whole + ' 1/8';
  return value.toFixed(2).replace(/\.?0+$/, '');
}

function scaleIngredientItem(item, factor) {
  const parsed = parseQuantity(item);
  if (!parsed || parsed.rest === '') return item;
  const scaled = parsed.value * factor;
  const formatted = formatScaledQuantity(scaled);
  return formatted + (parsed.rest ? ' ' + parsed.rest : '');
}

function RecipeDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [generatingNutrition, setGeneratingNutrition] = useState(false);
  const [printScale, setPrintScale] = useState(1);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [customizeInstruction, setCustomizeInstruction] = useState('');
  const [customizing, setCustomizing] = useState(false);
  const [showSaveToListModal, setShowSaveToListModal] = useState(false);
  const [saveToListLists, setSaveToListLists] = useState([]);
  const [saveToListSelectedId, setSaveToListSelectedId] = useState('');
  const [savingToList, setSavingToList] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadRecipe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadRecipe = async () => {
    try {
      setLoading(true);
      const response = await recipeAPI.get(parseInt(id));
      setRecipe(response.data.recipe);
      setNotesText(response.data.recipe.notes || '');
      setError(null);
    } catch (err) {
      setError('Failed to load recipe');
      console.error('Error loading recipe:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      await recipeAPI.update(recipe.id, { notes: notesText });
      setRecipe(prev => ({ ...prev, notes: notesText }));
      setIsEditingNotes(false);
    } catch (err) {
      alert('Failed to save notes');
      console.error('Error saving notes:', err);
    }
  };

  const handleAddNote = () => {
    setIsEditingNotes(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setUploadingImage(true);
    try {
      const response = await uploadAPI.uploadPhoto(file, recipe.id);
      // The backend now sets image_url automatically if it's the first image
      // Reload recipe to get updated photos and image_url
      await loadRecipe();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to upload image';
      alert(`Failed to upload image: ${errorMsg}`);
      console.error('Error uploading image:', err);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async () => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await uploadAPI.deleteRecipeImage(recipe.id);
      await loadRecipe();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to delete image';
      alert(`Failed to delete image: ${errorMsg}`);
      console.error('Error deleting image:', err);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await uploadAPI.deletePhoto(photoId);
      await loadRecipe();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to delete photo';
      alert(`Failed to delete photo: ${errorMsg}`);
      console.error('Error deleting photo:', err);
    }
  };

  const handleGenerateNutrition = async () => {
    if (!recipe.ingredients || recipe.ingredients.length === 0) {
      alert('Recipe needs ingredients to calculate nutrition information.');
      return;
    }

    setGeneratingNutrition(true);
    try {
      const response = await recipeAPI.generateNutrition(recipe.id);
      await loadRecipe();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Failed to generate nutrition';
      alert(`Failed to generate nutrition: ${errorMsg}`);
      console.error('Error generating nutrition:', err);
    } finally {
      setGeneratingNutrition(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        handleImageUpload(file);
      } else {
        alert('Please drop an image file');
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSaveToListOpen = async () => {
    setShowSaveToListModal(true);
    setSaveToListSelectedId('');
    try {
      const res = await boardAPI.getAll();
      const boards = res.data?.boards || [];
      const lists = boards.flatMap(b => (b.lists || []).map(lst => ({ ...lst, boardTitle: b.title })));
      setSaveToListLists(lists);
    } catch (err) {
      console.error('Failed to load lists', err);
      setSaveToListLists([]);
    }
  };

  const handleSaveToListSubmit = async () => {
    const listId = saveToListSelectedId ? parseInt(saveToListSelectedId, 10) : null;
    if (!listId || !recipe) return;
    setSavingToList(true);
    try {
      await recipeAPI.create({
        list_id: listId,
        title: recipe.title,
        description: recipe.description,
        servings: recipe.servings,
        prep_time: recipe.prep_time,
        cook_time: recipe.cook_time,
        total_time: recipe.total_time,
        image_url: recipe.image_url,
        source_url: recipe.source_url,
        rating: recipe.rating,
        notes: recipe.notes,
        ingredients: recipe.ingredients?.map(i => i.item) || [],
        instructions: recipe.instructions?.map(i => i.instruction) || [],
      });
      setShowSaveToListModal(false);
      setSaveToListSelectedId('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save recipe to list');
    } finally {
      setSavingToList(false);
    }
  };

  const handleCustomizeOpen = () => {
    setCustomizeInstruction('');
    setShowCustomizeModal(true);
  };

  const handleCustomizeClose = () => {
    if (!customizing) {
      setShowCustomizeModal(false);
      setCustomizeInstruction('');
    }
  };

  const handleCustomizeSubmit = async () => {
    if (!recipe) return;
    setCustomizing(true);
    try {
      const res = await recipeAPI.customize(recipe.id, { instruction: customizeInstruction.trim() || undefined });
      setRecipe(res.data.recipe);
      setNotesText(res.data.recipe.notes || '');
      setShowCustomizeModal(false);
      setCustomizeInstruction('');
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to customize recipe.');
    } finally {
      setCustomizing(false);
    }
  };

  const scaledIngredientItems = recipe?.ingredients?.length
    ? recipe.ingredients.map((ing) => ({
        ...ing,
        displayItem: printScale === 1 ? ing.item : scaleIngredientItem(ing.item, printScale),
      }))
    : [];

  const scaledServings =
    recipe?.servings && printScale !== 1
      ? (() => {
          const match = String(recipe.servings).match(/[\d.]+/);
          const n = match ? parseFloat(match[0], 10) : NaN;
          if (Number.isNaN(n)) return recipe.servings;
          const scaled = Math.round(n * printScale);
          return String(scaled);
        })()
      : recipe?.servings;

  const formatRecipeForSharing = () => {
    let text = `${recipe.title}\n\n`;
    
    if (recipe.description) {
      text += `${recipe.description}\n\n`;
    }
    
    if (recipe.prep_time || recipe.cook_time || recipe.total_time || recipe.servings) {
      text += 'Details:\n';
      if (recipe.prep_time) text += `Prep Time: ${recipe.prep_time}\n`;
      if (recipe.cook_time) text += `Cook Time: ${recipe.cook_time}\n`;
      if (recipe.total_time) text += `Total Time: ${recipe.total_time}\n`;
      if (recipe.servings) text += `Servings: ${recipe.servings}\n`;
      text += '\n';
    }
    
    if (recipe.ingredients && recipe.ingredients.length > 0) {
      text += 'Ingredients:\n';
      recipe.ingredients.forEach((ingredient, index) => {
        text += `${index + 1}. ${ingredient.item}\n`;
      });
      text += '\n';
    }
    
    if (recipe.instructions && recipe.instructions.length > 0) {
      text += 'Instructions:\n';
      recipe.instructions.forEach((instruction, index) => {
        const stepNum = instruction.step_number || index + 1;
        text += `${stepNum}. ${instruction.instruction}\n`;
      });
      text += '\n';
    }
    
    if (recipe.notes) {
      text += `Notes: ${recipe.notes}\n\n`;
    }
    
    if (recipe.source_url) {
      text += `Source: ${recipe.source_url}\n`;
    }
    
    text += `\nShared from RecipeWorks`;
    
    return text;
  };

  const handleShare = async () => {
    const shareText = formatRecipeForSharing();
    const shareTitle = recipe.title;
    
    // Always show our custom share modal with Email, SMS, and Copy options
    // This ensures SMS/text messaging is always available
    // Share full recipe text without RecipeWorks link
    fallbackShare(shareText, shareTitle);
  };

  const fallbackShare = (text, title) => {
    // Create a modal with sharing options
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
    `;
    
    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      padding: 2rem;
      border-radius: 12px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    `;
    
    // Build SMS link - share full recipe text (no link)
    const smsBody = text;
    const smsLink = `sms:?body=${encodeURIComponent(smsBody)}`;
    
    // Build email link - share full recipe text (no link)
    const emailBody = text;
    const emailLink = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(emailBody)}`;
    
    // Copy function - full recipe text (no link)
    const copyText = text;
    const copyFunction = `navigator.clipboard.writeText(\`${copyText.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`); alert('Recipe copied to clipboard!'); this.closest('div[style*=\\'position: fixed\\']').remove();`;
    
    // Native share function (for AirDrop on iOS/macOS) - shares full recipe text
    let nativeShareButton = '';
    if (navigator.share) {
      const nativeShareFunction = `navigator.share({title: \`${title.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`, text: \`${text.replace(/`/g, '\\`').replace(/\$/g, '\\$')}\`}).catch(() => {}); this.closest('div[style*=\\'position: fixed\\']').remove();`;
      nativeShareButton = `
        <button onclick="${nativeShareFunction}" 
                style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--primary-blue); border-radius: 8px; border: none; cursor: pointer; color: white; font-weight: 500; width: 100%; text-align: left; font-size: 1rem; margin-bottom: 0.75rem;">
          📱 Share via AirDrop/More
        </button>
      `;
    }
    
    content.innerHTML = `
      <h2 style="margin-top: 0; color: var(--primary-dark-blue);">Share Recipe</h2>
      <div style="display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 1.5rem;">
        ${nativeShareButton}
        <a href="${emailLink}" 
           onclick="this.closest('div[style*=\\'position: fixed\\']').remove();"
           style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--light-blue); border-radius: 8px; text-decoration: none; color: var(--primary-dark-blue); font-weight: 500;">
          📧 Share via Email
        </a>
        <a href="${smsLink}" 
           onclick="this.closest('div[style*=\\'position: fixed\\']').remove();"
           style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--light-blue); border-radius: 8px; text-decoration: none; color: var(--primary-dark-blue); font-weight: 500;">
          💬 Share via SMS/Text
        </a>
        <button onclick="${copyFunction}" 
                style="display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem; background: var(--light-blue); border-radius: 8px; border: none; cursor: pointer; color: var(--primary-dark-blue); font-weight: 500; width: 100%; text-align: left; font-size: 1rem;">
          📋 Copy to Clipboard
        </button>
      </div>
      <button onclick="this.closest('div[style*=\\'position: fixed\\']').remove();" 
              style="width: 100%; padding: 0.75rem; background: var(--medium-gray); border: none; border-radius: 8px; cursor: pointer; font-size: 1rem;">
        Cancel
      </button>
    `;
    
    modal.appendChild(content);
    document.body.appendChild(modal);
    
    // Close on outside click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) {
      return;
    }

    try {
      await recipeAPI.delete(recipe.id);
      navigate('/');
    } catch (err) {
      alert('Failed to delete recipe');
      console.error('Error deleting recipe:', err);
    }
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  if (loading) {
    return (
      <div className="app">
        <header className="header">
          <div className="header-content">
            <Link to="/" className="header-logo" style={{ textDecoration: 'none' }}>
              <img src="/logo.png" alt="RecipeWorks" className="header-logo-img" onError={(e) => { e.target.style.display = 'none'; }} />
            </Link>
            <div className="header-nav">
              <Link to="/" className="header-button" style={{ textDecoration: 'none' }}>
                ← Back to Dashboard
              </Link>
              <span>{user.username}</span>
              <button className="header-button" onClick={onLogout}>
                Logout
              </button>
            </div>
          </div>
        </header>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <div className="loading-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px' }}></div>
        </div>
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="app">
        <header className="header">
          <div className="header-content">
            <Link to="/" className="header-logo" style={{ textDecoration: 'none' }}>
              <img src="/logo.png" alt="RecipeWorks" className="header-logo-img" onError={(e) => { e.target.style.display = 'none'; }} />
            </Link>
            <div className="header-nav">
              <Link to="/" className="header-button" style={{ textDecoration: 'none' }}>
                ← Back to Dashboard
              </Link>
              <span>{user.username}</span>
              <button className="header-button" onClick={onLogout}>
                Logout
              </button>
            </div>
          </div>
        </header>
        <div className="recipe-detail-container">
          <div className="recipe-detail-card">
            <h2>Recipe Not Found</h2>
            <p>{error || 'The recipe you are looking for does not exist.'}</p>
            <Link to="/" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-block', marginTop: '1rem' }}>
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <Link to="/" className="header-logo" style={{ textDecoration: 'none' }}>
            <img src="/logo.png" alt="RecipeWorks" className="header-logo-img" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="header-logo-text">RecipeWorks</span>
          </Link>
          <div className="header-nav">
            <Link to="/" className="header-button" style={{ textDecoration: 'none' }}>
              ← Back to Dashboard
            </Link>
            <span>{user.username}</span>
            <button className="header-button" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="recipe-detail-container">
        <div className="recipe-detail-card">
          {/* Recipe Header: two containers — left: title, right: all buttons */}
          <div className="recipe-detail-header">
            <div className="recipe-detail-header-left">
              <h1 className="recipe-detail-title">{recipe.title}</h1>
              {recipe.rating && (
                <div className="recipe-detail-rating">
                  {renderStars(recipe.rating)}
                </div>
              )}
            </div>
            <div className="recipe-detail-header-right">
              <button onClick={handleShare} className="btn btn-primary btn-share-compact">
                📤 Share
              </button>
              <span className="print-scale-label">Print scale:</span>
              <div className="print-scale-buttons" role="group" aria-label="Recipe scale">
                <button
                  type="button"
                  className={`btn btn-scale ${printScale === 0.5 ? 'btn-scale-active' : ''}`}
                  onClick={() => setPrintScale(0.5)}
                  title="Half recipe"
                >
                  ½×
                </button>
                <button
                  type="button"
                  className={`btn btn-scale ${printScale === 1 ? 'btn-scale-active' : ''}`}
                  onClick={() => setPrintScale(1)}
                  title="Original"
                >
                  1×
                </button>
                <button
                  type="button"
                  className={`btn btn-scale ${printScale === 2 ? 'btn-scale-active' : ''}`}
                  onClick={() => setPrintScale(2)}
                  title="Double recipe"
                >
                  2×
                </button>
              </div>
              <button onClick={handlePrint} className="btn btn-secondary">
                🖨️ Print
              </button>
              <button type="button" onClick={handleSaveToListOpen} className="btn btn-secondary" title="Save a copy to another list">
                💾 Save to list
              </button>
              <button type="button" onClick={handleCustomizeOpen} className="btn btn-secondary" title="Tweak recipe to your tastes with AI">
                ✨ Tweak with AI
              </button>
              <button onClick={() => navigate(`/recipe/${recipe.id}/edit`)} className="btn btn-secondary">
                Edit
              </button>
              <button onClick={handleDelete} className="btn btn-danger">
                Delete
              </button>
            </div>
          </div>

          {/* Recipe Image */}
          <div className="recipe-detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Recipe Image</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {recipe.image_url && (
                  <button 
                    onClick={handleDeleteImage} 
                    className="btn btn-danger" 
                    style={{ padding: '0.5rem 1rem', margin: 0 }}
                  >
                    🗑️ Delete
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                  id="recipe-image-upload"
                />
                <label htmlFor="recipe-image-upload" className="btn btn-primary" style={{ cursor: 'pointer', padding: '0.5rem 1rem', margin: 0 }}>
                  {uploadingImage ? <span className="loading-spinner"></span> : recipe.image_url ? '🔄 Replace' : '📷 Upload Image'}
                </label>
              </div>
            </div>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              style={{
                border: isDragging ? '3px dashed var(--primary-blue)' : recipe.image_url ? 'none' : '2px dashed var(--medium-gray)',
                borderRadius: '8px',
                padding: recipe.image_url ? '0' : '3rem',
                textAlign: 'center',
                color: '#999',
                background: isDragging ? 'var(--light-blue)' : recipe.image_url ? 'transparent' : 'var(--light-gray)',
                transition: 'all 0.2s',
                cursor: 'pointer',
                position: 'relative'
              }}
              onClick={() => !recipe.image_url && fileInputRef.current?.click()}
            >
              {uploadingImage ? (
                <div style={{ padding: '2rem' }}>
                  <div className="loading-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px', margin: '0 auto' }}></div>
                  <p style={{ marginTop: '1rem' }}>Uploading...</p>
                </div>
              ) : recipe.image_url ? (
                <div className="recipe-detail-image">
                  <img src={recipe.image_url} alt={recipe.title} />
                  {isDragging && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(74, 144, 226, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      fontSize: '1.5rem',
                      color: 'white',
                      fontWeight: 'bold'
                    }}>
                      Drop image here
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <p style={{ fontSize: '3rem', margin: '0 0 1rem 0' }}>📷</p>
                  <p>{isDragging ? 'Drop image here' : 'Drop an image here or click to upload'}</p>
                </>
              )}
            </div>
          </div>

          {/* Recipe Meta Information */}
          <div className="recipe-detail-meta">
            {recipe.prep_time && (
              <div className="meta-item">
                <span className="meta-label">Prep Time:</span>
                <span className="meta-value">{recipe.prep_time}</span>
              </div>
            )}
            {recipe.cook_time && (
              <div className="meta-item">
                <span className="meta-label">Cook Time:</span>
                <span className="meta-value">{recipe.cook_time}</span>
              </div>
            )}
            {recipe.total_time && (
              <div className="meta-item">
                <span className="meta-label">Total Time:</span>
                <span className="meta-value">{recipe.total_time}</span>
              </div>
            )}
            {recipe.servings && (
              <div className="meta-item">
                <span className="meta-label">Servings:</span>
                <span className="meta-value">{scaledServings}{printScale !== 1 ? ` (${printScale === 0.5 ? '½' : printScale}×)` : ''}</span>
              </div>
            )}
          </div>

          {/* Description */}
          {recipe.description && (
            <div className="recipe-detail-section">
              <h2>Description</h2>
              <p>{recipe.description}</p>
            </div>
          )}

          {/* Ingredients */}
          {recipe.ingredients && recipe.ingredients.length > 0 && (
            <div className="recipe-detail-section">
              <h2>Ingredients</h2>
              {printScale !== 1 && (
                <p className="recipe-scale-note">
                  Scaled to {printScale === 0.5 ? '½' : printScale}× for printing.
                </p>
              )}
              <ul className="ingredient-list">
                {scaledIngredientItems.map((ingredient, index) => (
                  <li key={ingredient.id || index} className="ingredient-item">
                    {ingredient.displayItem}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Instructions */}
          {recipe.instructions && recipe.instructions.length > 0 && (
            <div className="recipe-detail-section">
              <h2>Instructions</h2>
              <ol className="instruction-list">
                {recipe.instructions.map((instruction, index) => (
                  <li key={instruction.id || index} className="instruction-item">
                    <span className="step-number">{instruction.step_number || index + 1}</span>
                    <span className="instruction-text">{instruction.instruction}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Notes */}
          <div className="recipe-detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Notes</h2>
              {!isEditingNotes && (
                <button onClick={handleAddNote} className="btn btn-primary" style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', maxWidth: '96px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {recipe.notes ? 'Edit Note' : '+ Add Note'}
                </button>
              )}
            </div>
            {isEditingNotes ? (
              <div>
                <textarea
                  value={notesText}
                  onChange={(e) => setNotesText(e.target.value)}
                  placeholder="Add your cooking notes, modifications, or tips..."
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '1rem',
                    border: '1px solid var(--medium-gray)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button onClick={handleSaveNotes} className="btn btn-success">
                    Save Note
                  </button>
                  <button 
                    onClick={() => {
                      setIsEditingNotes(false);
                      setNotesText(recipe.notes || '');
                    }} 
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : recipe.notes ? (
              <p className="recipe-notes">{recipe.notes}</p>
            ) : (
              <p style={{ color: '#999', fontStyle: 'italic' }}>No notes yet. Click "Add Note" to add cooking notes, modifications, or tips.</p>
            )}
          </div>

          {/* Source URL */}
          {recipe.source_url && (
            <div className="recipe-detail-section">
              <h2>Source</h2>
              <a href={recipe.source_url} target="_blank" rel="noopener noreferrer" className="source-link">
                {recipe.source_url}
              </a>
            </div>
          )}

          {/* Nutrition Information */}
          <div className="recipe-detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Nutrition Information</h2>
              {(() => {
                // Check if nutrition exists and has meaningful data
                let hasValidNutrition = false;
                if (recipe.nutrition) {
                  try {
                    const nutrition = typeof recipe.nutrition === 'string' ? JSON.parse(recipe.nutrition) : recipe.nutrition;
                    if (nutrition && typeof nutrition === 'object') {
                      // Check if any nutrition value is not null/empty
                      hasValidNutrition = Object.values(nutrition).some(val => 
                        val !== null && val !== undefined && val !== '' && val !== 'null'
                      );
                    }
                  } catch (e) {
                    // If parsing fails, treat as no valid nutrition
                    hasValidNutrition = false;
                  }
                }
                // Show button if no nutrition or nutrition has no valid values
                if (!hasValidNutrition) {
                  return (
                    <button 
                      onClick={handleGenerateNutrition} 
                      className="btn btn-primary" 
                      disabled={generatingNutrition}
                      style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', maxWidth: '150px', whiteSpace: 'nowrap' }}
                    >
                      {generatingNutrition ? <span className="loading-spinner"></span> : '🧮 Generate Nutrition'}
                    </button>
                  );
                }
                return null;
              })()}
            </div>
            {recipe.nutrition ? (() => {
              try {
                const nutrition = typeof recipe.nutrition === 'string' ? JSON.parse(recipe.nutrition) : recipe.nutrition;
                if (nutrition && Object.keys(nutrition).length > 0) {
                  // Check if we have any non-null values
                  const hasValidValues = Object.values(nutrition).some(val => 
                    val !== null && val !== undefined && val !== '' && val !== 'null'
                  );
                  
                  if (hasValidValues) {
                    return (
                      <div className="nutrition-grid">
                        {nutrition.calories && nutrition.calories !== 'null' && (
                          <div className="nutrition-item">
                            <span className="nutrition-label">Calories</span>
                            <span className="nutrition-value">{nutrition.calories}</span>
                          </div>
                        )}
                        {nutrition.protein && nutrition.protein !== 'null' && (
                          <div className="nutrition-item">
                            <span className="nutrition-label">Protein</span>
                            <span className="nutrition-value">{nutrition.protein}</span>
                          </div>
                        )}
                        {nutrition.fat && nutrition.fat !== 'null' && (
                          <div className="nutrition-item">
                            <span className="nutrition-label">Fat</span>
                            <span className="nutrition-value">{nutrition.fat}</span>
                          </div>
                        )}
                        {nutrition.carbs && nutrition.carbs !== 'null' && (
                          <div className="nutrition-item">
                            <span className="nutrition-label">Carbs</span>
                            <span className="nutrition-value">{nutrition.carbs}</span>
                          </div>
                        )}
                        {nutrition.fiber && nutrition.fiber !== 'null' && (
                          <div className="nutrition-item">
                            <span className="nutrition-label">Fiber</span>
                            <span className="nutrition-value">{nutrition.fiber}</span>
                          </div>
                        )}
                        {nutrition.sugar && nutrition.sugar !== 'null' && (
                          <div className="nutrition-item">
                            <span className="nutrition-label">Sugar</span>
                            <span className="nutrition-value">{nutrition.sugar}</span>
                          </div>
                        )}
                        {nutrition.sodium && nutrition.sodium !== 'null' && (
                          <div className="nutrition-item">
                            <span className="nutrition-label">Sodium</span>
                            <span className="nutrition-value">{nutrition.sodium}</span>
                          </div>
                        )}
                      </div>
                    );
                  }
                }
              } catch (e) {
                console.error('Error parsing nutrition data:', e);
              }
              return (
                <p style={{ color: '#999', fontStyle: 'italic' }}>
                  No nutrition information yet. Click "Generate Nutrition" to calculate nutritional values from the ingredients.
                </p>
              );
            })() : (
              <p style={{ color: '#999', fontStyle: 'italic' }}>
                No nutrition information yet. Click "Generate Nutrition" to calculate nutritional values from the ingredients.
              </p>
            )}
          </div>

          {/* Additional Photos */}
          <div className="recipe-detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2>Additional Photos</h2>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                  id="additional-photo-upload"
                />
                <label htmlFor="additional-photo-upload" className="btn btn-primary" style={{ cursor: 'pointer', padding: '0.5rem 1rem', margin: 0 }}>
                  {uploadingImage ? <span className="loading-spinner"></span> : '+ Add Photo'}
                </label>
              </div>
            </div>
            {recipe.photos && recipe.photos.length > 0 ? (
              <div className="recipe-photos-grid">
                {recipe.photos.map((photo) => (
                  <div key={photo.id} className="recipe-photo-item" style={{ position: 'relative' }}>
                    <img src={photo.photo_url} alt={photo.caption || recipe.title} />
                    <button
                      onClick={() => handleDeletePhoto(photo.id)}
                      className="btn btn-danger"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        opacity: 0.9
                      }}
                      title="Delete photo"
                    >
                      🗑️
                    </button>
                    {photo.caption && <p className="photo-caption">{photo.caption}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#999', fontStyle: 'italic' }}>No additional photos yet. Click "Add Photo" to upload more images.</p>
            )}
          </div>
        </div>
      </div>

      {showCustomizeModal && (
        <div className="modal-overlay" onClick={handleCustomizeClose} role="presentation" aria-modal="true">
          <div className="modal-content save-recipe-modal customize-modal" onClick={(e) => e.stopPropagation()}>
            <h3>✨ Tweak recipe with AI</h3>
            <p>We’ll tailor this recipe to your tastes (savory-sweet, your condiments). Add any specific changes below, or leave blank.</p>
            <label htmlFor="customize-instruction" className="create-from-ingredients-label">How would you like to customize? (optional)</label>
            <textarea
              id="customize-instruction"
              className="create-from-ingredients-textarea"
              placeholder="e.g. make it spicier, less salt, add soy sauce, more savory-sweet"
              value={customizeInstruction}
              onChange={(e) => setCustomizeInstruction(e.target.value)}
              rows={3}
              disabled={customizing}
            />
            {customizing && (
              <div className="create-from-ingredients-loading" style={{ marginBottom: '1rem' }}>
                <div className="loading-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} aria-hidden />
                <span>AI is updating your recipe…</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-primary" onClick={handleCustomizeSubmit} disabled={customizing}>
                {customizing ? 'Updating…' : 'Update recipe'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCustomizeClose} disabled={customizing}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveToListModal && (
        <div className="modal-overlay" onClick={() => setShowSaveToListModal(false)} role="presentation" aria-modal="true">
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2>Save recipe to list</h2>
              <button type="button" className="modal-close" onClick={() => setShowSaveToListModal(false)} aria-label="Close">×</button>
            </div>
            <p style={{ marginBottom: '1rem', color: 'var(--dark-gray)' }}>Save a copy of this recipe to another list.</p>
            <div className="form-group">
              <label htmlFor="save-to-list-select">Choose list</label>
              <select
                id="save-to-list-select"
                value={saveToListSelectedId}
                onChange={(e) => setSaveToListSelectedId(e.target.value)}
                disabled={savingToList}
              >
                <option value="">-- Select a list --</option>
                {saveToListLists.map(lst => (
                  <option key={lst.id} value={lst.id}>
                    {lst.boardTitle} → {lst.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowSaveToListModal(false)} disabled={savingToList}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveToListSubmit}
                disabled={!saveToListSelectedId || savingToList}
              >
                {savingToList ? (
                  <>
                    <span className="loading-spinner" style={{ width: '18px', height: '18px', borderWidth: '2px', marginRight: '6px', verticalAlign: 'middle' }} />
                    Saving…
                  </>
                ) : (
                  'Save copy'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecipeDetail;
